import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  FileText, Plus, Download, Clock, Calendar, Trash2, 
  Play, BarChart3, AlertTriangle, CheckCircle, 
  FileSpreadsheet, File, Loader2, History, Building2, Crown, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { UpgradePrompt } from '../components/UpgradePrompt';

// Simplified Report Types
const REPORT_TYPES = [
  { id: 'violation_summary', name: 'Violation Summary', icon: AlertTriangle, color: 'rose' },
  { id: 'compliance_status', name: 'Compliance Status', icon: CheckCircle, color: 'emerald' },
];

// Schedule Options
const SCHEDULE_OPTIONS = [
  { value: 'once', label: 'Generate Now' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Enhanced Report Frequency Options (Enterprise Only)
const ENHANCED_FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

// Export Formats
const EXPORT_FORMATS = [
  { value: 'pdf', label: 'PDF', icon: File },
  { value: 'csv', label: 'CSV', icon: FileSpreadsheet },
  { value: 'xlsx', label: 'Excel', icon: FileSpreadsheet }
];

// Available Datasets
const AVAILABLE_DATASETS = [
  { id: 'dob_violations', name: 'DOB Violations' },
  { id: 'ecb_violations', name: 'ECB Violations' },
  { id: 'fire_violations', name: 'Fire Violations' },
  { id: 'permits', name: 'Permits' },
  { id: 'oath_hearings', name: 'OATH Hearings' },
  { id: 'oer_sites', name: 'OER Cleanup Sites' },
  { id: 'dep_environmental', name: 'DEP Environmental' },
  { id: 'e_designations', name: 'E-Designations' },
  { id: 'hpd_violations', name: 'HPD Violations' },
  { id: 'bsa_applications', name: 'BSA Applications' }
];

// Quick Start Templates (reduced from 10 to 4)
const QUICK_TEMPLATES = [
  {
    id: 'weekly_digest',
    name: 'Weekly Violation Digest',
    description: 'All new violations from the past week',
    config: { dateRange: '7', datasets: ['dob_violations', 'ecb_violations'], format: 'pdf', schedule: 'weekly' }
  },
  {
    id: 'open_violations',
    name: 'Open Violations Report',
    description: 'Currently open violations requiring action',
    config: { dateRange: 'all', datasets: ['dob_violations', 'ecb_violations', 'fire_violations'], format: 'xlsx', schedule: 'once' }
  },
  {
    id: 'monthly_summary',
    name: 'Monthly Summary',
    description: 'Monthly portfolio analysis',
    config: { dateRange: '30', datasets: ['dob_violations', 'ecb_violations'], format: 'pdf', schedule: 'monthly' }
  },
  {
    id: 'compliance_check',
    name: 'Compliance Scorecard',
    description: 'Property compliance ratings',
    config: { dateRange: 'all', datasets: ['dob_violations', 'fire_violations'], format: 'pdf', schedule: 'once' }
  }
];

export default function ReportsPage() {
  const { user, api } = useAuth();
  const [activeTab, setActiveTab] = useState('enhanced');
  const [loading, setLoading] = useState(true);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [reportHistory, setReportHistory] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Enhanced Property Reports state
  const [enhancedSchedules, setEnhancedSchedules] = useState([]);
  const [enhancedDialogOpen, setEnhancedDialogOpen] = useState(false);
  const [creatingEnhanced, setCreatingEnhanced] = useState(false);
  const [newEnhancedSchedule, setNewEnhancedSchedule] = useState({
    name: '',
    frequency: 'monthly',
    email_recipients: [],
    emailInput: '',
    watchlist_id: '',
    bin_number: '',
    scheduleType: 'watchlist', // 'watchlist' or 'property'
    pdf_report_type: 'executive', // 'executive' or 'extended'
  });

  // New report form
  const [newReport, setNewReport] = useState({
    name: '',
    type: 'violation_summary',
    format: 'pdf',
    schedule: 'once',
    emailRecipients: user?.email || '',
    watchlistId: '',
    dateRange: '30',
    datasets: ['dob_violations'],
    includeResolved: false,
  });

  const isEnterprise = user?.tier === 'enterprise' || user?.role === 'admin';

  // Preview report state
  const [previewAddress, setPreviewAddress] = useState('250 West 34th Street');
  const [previewBorough, setPreviewBorough] = useState('Manhattan');
  const [generatingPreview, setGeneratingPreview] = useState(null);
  const [includeRiskScore, setIncludeRiskScore] = useState(true);

  const handlePreviewReport = async (reportType) => {
    setGeneratingPreview(reportType);
    try {
      const token = localStorage.getItem('token');
      const response = await api().get(`/export/enhanced-report-by-address`, {
        params: {
          address: previewAddress,
          borough: previewBorough,
          report_type: reportType,
          include_risk: includeRiskScore,
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Insite360_${reportType}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${reportType === 'executive' ? 'Executive' : 'Extended'} report downloaded`);
    } catch (err) {
      console.error('Report generation failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate report. Try a different address.');
    } finally {
      setGeneratingPreview(null);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [scheduledRes, historyRes, watchlistsRes, enhancedRes] = await Promise.all([
        api().get('/reports/scheduled').catch(() => ({ data: { scheduled_reports: [] } })),
        api().get('/reports/history').catch(() => ({ data: { history: [] } })),
        api().get('/watchlists').catch(() => ({ data: [] })),
        api().get('/reports/scheduled/enhanced').catch(() => ({ data: { schedules: [] } }))
      ]);
      
      setScheduledReports(scheduledRes.data?.scheduled_reports || []);
      setReportHistory(historyRes.data?.history || []);
      setWatchlists(watchlistsRes.data || []);
      setEnhancedSchedules(enhancedRes.data?.schedules || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (isEnterprise) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [fetchData, isEnterprise]);

  // Generate or schedule report
  const handleGenerateReport = async () => {
    if (!newReport.name) {
      toast.error('Please enter a report name');
      return;
    }

    if (newReport.datasets.length === 0) {
      toast.error('Please select at least one dataset');
      return;
    }

    setGenerating(true);
    try {
      if (newReport.schedule === 'once') {
        const response = await api().post('/reports/generate-direct', {
          name: newReport.name,
          report_type: newReport.type,
          format: newReport.format,
          datasets: newReport.datasets,
          columns: ['address', 'violation_type', 'issue_date', 'status', 'severity'],
          date_range: newReport.dateRange,
          include_resolved: newReport.includeResolved,
          watchlist_id: newReport.watchlistId || null,
          filters: {}
        }, { responseType: 'blob' });

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${newReport.name.replace(/\s+/g, '_')}.${newReport.format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success('Report downloaded!');
      } else {
        await api().post('/reports/scheduled', {
          name: newReport.name,
          report_type: newReport.type,
          format: newReport.format,
          schedule: newReport.schedule,
          datasets: newReport.datasets,
          columns: ['address', 'violation_type', 'issue_date', 'status', 'severity'],
          date_range: newReport.dateRange,
          include_resolved: newReport.includeResolved,
          watchlist_id: newReport.watchlistId || null,
          email_recipients: newReport.emailRecipients
        });

        toast.success('Report scheduled!');
        fetchData();
      }
      
      setCreateDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error('Failed to generate report:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setNewReport({
      name: '',
      type: 'violation_summary',
      format: 'pdf',
      schedule: 'once',
      emailRecipients: user?.email || '',
      watchlistId: '',
      dateRange: '30',
      datasets: ['dob_violations'],
      includeResolved: false,
    });
  };

  const applyQuickTemplate = (template) => {
    setNewReport(prev => ({
      ...prev,
      name: template.name,
      ...template.config
    }));
    setCreateDialogOpen(true);
  };

  const toggleScheduledReport = async (id) => {
    try {
      const response = await api().patch(`/reports/scheduled/${id}/toggle`);
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to toggle report');
    }
  };

  const deleteScheduledReport = async (id) => {
    if (!window.confirm('Delete this scheduled report?')) return;
    try {
      await api().delete(`/reports/scheduled/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const runScheduledReportNow = async (id, name, format) => {
    try {
      toast.info(`Generating...`);
      const response = await api().post(`/reports/scheduled/${id}/run`, {}, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/\s+/g, '_')}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Downloaded!');
      fetchData();
    } catch (err) {
      toast.error('Failed to run report');
    }
  };

  const handleDatasetToggle = (datasetId) => {
    setNewReport(prev => {
      const datasets = prev.datasets.includes(datasetId)
        ? prev.datasets.filter(d => d !== datasetId)
        : [...prev.datasets, datasetId];
      return { ...prev, datasets };
    });
  };

  // ============== ENHANCED PROPERTY REPORT HANDLERS ==============
  
  const handleCreateEnhancedSchedule = async () => {
    if (!newEnhancedSchedule.name) {
      toast.error('Please enter a schedule name');
      return;
    }
    
    if (newEnhancedSchedule.email_recipients.length === 0) {
      toast.error('Please add at least one email recipient');
      return;
    }
    
    if (newEnhancedSchedule.scheduleType === 'watchlist' && !newEnhancedSchedule.watchlist_id) {
      toast.error('Please select a watchlist');
      return;
    }
    
    if (newEnhancedSchedule.scheduleType === 'property' && !newEnhancedSchedule.bin_number) {
      toast.error('Please enter a BIN number');
      return;
    }
    
    setCreatingEnhanced(true);
    try {
      const payload = {
        name: newEnhancedSchedule.name,
        frequency: newEnhancedSchedule.frequency,
        email_recipients: newEnhancedSchedule.email_recipients,
        pdf_report_type: newEnhancedSchedule.pdf_report_type,
      };
      
      if (newEnhancedSchedule.scheduleType === 'watchlist') {
        payload.watchlist_id = newEnhancedSchedule.watchlist_id;
      } else {
        payload.bin_number = newEnhancedSchedule.bin_number;
      }
      
      await api().post('/reports/scheduled/enhanced', payload);
      toast.success('Enhanced report schedule created!');
      setEnhancedDialogOpen(false);
      setNewEnhancedSchedule({
        name: '',
        frequency: 'monthly',
        email_recipients: [],
        emailInput: '',
        watchlist_id: '',
        bin_number: '',
        scheduleType: 'watchlist',
        pdf_report_type: 'executive',      });
      fetchData();
    } catch (err) {
      console.error('Failed to create enhanced schedule:', err);
      toast.error(err.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setCreatingEnhanced(false);
    }
  };
  
  const handleAddEmailRecipient = () => {
    const email = newEnhancedSchedule.emailInput.trim();
    if (!email) return;
    
    // Basic email validation
    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    if (newEnhancedSchedule.email_recipients.includes(email)) {
      toast.error('Email already added');
      return;
    }
    
    setNewEnhancedSchedule(prev => ({
      ...prev,
      email_recipients: [...prev.email_recipients, email],
      emailInput: ''
    }));
  };
  
  const handleRemoveEmailRecipient = (email) => {
    setNewEnhancedSchedule(prev => ({
      ...prev,
      email_recipients: prev.email_recipients.filter(e => e !== email)
    }));
  };
  
  const toggleEnhancedSchedule = async (id) => {
    try {
      const response = await api().patch(`/reports/scheduled/enhanced/${id}/toggle`);
      toast.success(response.data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to toggle schedule');
    }
  };
  
  const deleteEnhancedSchedule = async (id) => {
    if (!window.confirm('Delete this enhanced report schedule?')) return;
    try {
      await api().delete(`/reports/scheduled/enhanced/${id}`);
      toast.success('Schedule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete schedule');
    }
  };

  // Non-enterprise view
  if (!isEnterprise) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="reports-page">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-400" />
              Reports
            </h1>
            <p className="text-sm text-zinc-500">Generate and export compliance reports</p>
          </div>
          <div className="glass-card rounded-xl">
            <UpgradePrompt requiredTier="enterprise" feature="Scheduled reports & advanced report generation" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-400" />
              Reports
            </h1>
            <p className="text-sm text-zinc-500">Generate and export compliance reports</p>
          </div>
          <Button onClick={() => setEnhancedDialogOpen(true)} className="btn-primary" data-testid="create-report-btn">
            <Plus className="w-4 h-4 mr-2" />
            Schedule Report
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 p-1 rounded-lg w-fit">
          {[
            { id: 'enhanced', label: 'Property Reports', icon: Building2, count: enhancedSchedules.length },
            { id: 'scheduled', label: 'Scheduled', icon: Calendar, count: scheduledReports.length },
            { id: 'generate', label: 'Data Export', icon: FileSpreadsheet },
            { id: 'history', label: 'History', icon: History, count: reportHistory.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === tab.id 
                  ? 'bg-white/10 text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="text-xs bg-indigo-500/30 px-1.5 rounded">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
          </div>
        ) : (
          <>
            {/* Generate Tab */}
            {activeTab === 'generate' && (
              <div className="space-y-6">
                {/* Quick Start */}
                <div>
                  <h3 className="text-sm font-medium text-zinc-400 mb-3">Quick Start</h3>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {QUICK_TEMPLATES.map(template => (
                      <button
                        key={template.id}
                        onClick={() => applyQuickTemplate(template)}
                        className="glass-card p-4 rounded-xl text-left hover:border-indigo-500/40 border border-transparent transition-all group"
                        data-testid={`template-${template.id}`}
                      >
                        <h4 className="font-medium text-white text-sm mb-1">{template.name}</h4>
                        <p className="text-xs text-zinc-500 line-clamp-2">{template.description}</p>
                        <div className="flex gap-2 mt-3">
                          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded uppercase">{template.config.format}</span>
                          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded capitalize">{template.config.schedule}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Report */}
                <div className="glass-card p-6 rounded-xl">
                  <h3 className="text-sm font-medium text-zinc-400 mb-4">Custom Report</h3>
                  <p className="text-sm text-zinc-500 mb-4">
                    Need something specific? Create a custom report with your own settings.
                  </p>
                  <Button onClick={() => setCreateDialogOpen(true)} variant="outline" className="btn-ghost">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Custom Report
                  </Button>
                </div>
              </div>
            )}

            {/* Enhanced Property Reports Tab */}
            {activeTab === 'enhanced' && (
              <div className="space-y-4">
                {/* Generate Report Now */}
                <div className="glass-card rounded-xl p-5 border border-indigo-500/20">
                  <h3 className="font-medium text-white mb-1">Generate Report</h3>
                  <p className="text-xs text-zinc-500 mb-4">Enter a property address to download an Executive or Extended compliance report</p>
                  
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <div className="flex-1">
                      <Label className="text-xs text-zinc-400 mb-1">Address</Label>
                      <Input
                        value={previewAddress}
                        onChange={(e) => setPreviewAddress(e.target.value)}
                        placeholder="e.g. 250 West 34th Street"
                        className="bg-white/5 border-white/10 text-white text-sm"
                        data-testid="report-preview-address"
                      />
                    </div>
                    <div className="w-40">
                      <Label className="text-xs text-zinc-400 mb-1">Borough</Label>
                      <Select value={previewBorough} onValueChange={setPreviewBorough}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Manhattan">Manhattan</SelectItem>
                          <SelectItem value="Brooklyn">Brooklyn</SelectItem>
                          <SelectItem value="Bronx">Bronx</SelectItem>
                          <SelectItem value="Queens">Queens</SelectItem>
                          <SelectItem value="Staten Island">Staten Island</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <Switch
                      checked={includeRiskScore}
                      onCheckedChange={setIncludeRiskScore}
                      data-testid="include-risk-score-toggle"
                    />
                    <Label className="text-xs text-zinc-400 cursor-pointer" onClick={() => setIncludeRiskScore(!includeRiskScore)}>Include Risk Score</Label>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => handlePreviewReport('executive')}
                      disabled={generatingPreview || !previewAddress}
                      className="flex items-center gap-3 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/30 hover:bg-indigo-500/20 transition-all text-left disabled:opacity-50"
                      data-testid="download-executive-report"
                    >
                      {generatingPreview === 'executive' ? (
                        <Loader2 className="w-5 h-5 text-indigo-400 animate-spin shrink-0" />
                      ) : (
                        <Download className="w-5 h-5 text-indigo-400 shrink-0" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-white">Executive Report</div>
                        <div className="text-[11px] text-zinc-500">Summary with counts, AI analysis, maps</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => handlePreviewReport('extended')}
                      disabled={generatingPreview || !previewAddress}
                      className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 transition-all text-left disabled:opacity-50"
                      data-testid="download-extended-report"
                    >
                      {generatingPreview === 'extended' ? (
                        <Loader2 className="w-5 h-5 text-amber-400 animate-spin shrink-0" />
                      ) : (
                        <Download className="w-5 h-5 text-amber-400 shrink-0" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-white">Extended Report</div>
                        <div className="text-[11px] text-zinc-500">Executive + individual open violations & applications</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Scheduled Reports Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <Building2 className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">Scheduled Reports</h3>
                      <p className="text-xs text-zinc-500">Automatically generated and emailed on a recurring basis</p>
                    </div>
                  </div>
                  <Button onClick={() => setEnhancedDialogOpen(true)} className="btn-primary" data-testid="create-enhanced-schedule-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule Report
                  </Button>
                </div>
                
                {/* List of scheduled enhanced reports */}
                {enhancedSchedules.length === 0 ? (
                  <div className="glass-card rounded-xl p-8 text-center">
                    <Crown className="w-10 h-10 text-amber-500/50 mx-auto mb-3" />
                    <h3 className="font-medium text-white mb-1">No Scheduled Enhanced Reports</h3>
                    <p className="text-sm text-zinc-500 mb-4">
                      Schedule comprehensive property compliance reports to be generated and emailed monthly, quarterly, or yearly.
                    </p>
                    <Button onClick={() => setEnhancedDialogOpen(true)} size="sm" className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30">
                      Create Schedule
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {enhancedSchedules.map(schedule => (
                      <div key={schedule.id} className="glass-card p-4 rounded-xl flex items-center justify-between" data-testid={`enhanced-schedule-${schedule.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-amber-500/20 rounded-lg">
                            <Building2 className="w-4 h-4 text-amber-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white">{schedule.name}</h4>
                            <p className="text-xs text-zinc-500">
                              {schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)} • 
                              {schedule.pdf_report_type === 'extended' ? 'Extended' : 'Executive'} • 
                              {schedule.watchlist_id ? ' Watchlist' : ` BIN ${schedule.bin_number}`} • 
                              Next: {new Date(schedule.next_run).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-zinc-600 mt-1">
                              <Mail className="w-3 h-3 inline mr-1" />
                              {schedule.email_recipients?.join(', ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${schedule.enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                            {schedule.enabled ? 'Active' : 'Paused'}
                          </span>
                          <Switch
                            checked={schedule.enabled}
                            onCheckedChange={() => toggleEnhancedSchedule(schedule.id)}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteEnhancedSchedule(schedule.id)}
                            className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Info box */}
                <div className="glass-card p-4 rounded-xl border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <Crown className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-white text-sm">Enterprise Feature</h4>
                      <p className="text-xs text-zinc-400 mt-1">
                        Enhanced reports include comprehensive property data: violations, permits, flood zones, and ownership information in a professionally formatted PDF.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Scheduled Tab */}
            {activeTab === 'scheduled' && (
              <div>
                {scheduledReports.length === 0 ? (
                  <div className="glass-card rounded-xl p-8 text-center">
                    <Calendar className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                    <h3 className="font-medium text-white mb-1">No Scheduled Reports</h3>
                    <p className="text-sm text-zinc-500 mb-4">Set up automated reports to run on a schedule</p>
                    <Button onClick={() => setCreateDialogOpen(true)} size="sm" className="btn-primary">
                      Create Schedule
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {scheduledReports.map(report => (
                      <div key={report.id} className="glass-card p-4 rounded-xl flex items-center justify-between" data-testid={`scheduled-${report.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-indigo-500/20 rounded-lg">
                            <Clock className="w-4 h-4 text-indigo-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white">{report.name}</h4>
                            <p className="text-xs text-zinc-500">
                              {report.schedule} • {report.format.toUpperCase()} • Next: {new Date(report.next_run).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={report.enabled}
                            onCheckedChange={() => toggleScheduledReport(report.id)}
                          />
                          <Button variant="ghost" size="sm" onClick={() => runScheduledReportNow(report.id, report.name, report.format)} className="text-indigo-400 hover:text-indigo-300">
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteScheduledReport(report.id)} className="text-zinc-500 hover:text-rose-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div>
                {reportHistory.length === 0 ? (
                  <div className="glass-card rounded-xl p-8 text-center">
                    <History className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                    <h3 className="font-medium text-white mb-1">No Report History</h3>
                    <p className="text-sm text-zinc-500">Generated reports will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {reportHistory.map(report => (
                      <div key={report.id} className="glass-card p-4 rounded-xl flex items-center justify-between" data-testid={`history-${report.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-emerald-500/20 rounded-lg">
                            <FileText className="w-4 h-4 text-emerald-400" />
                          </div>
                          <div>
                            <h4 className="font-medium text-white">{report.name}</h4>
                            <p className="text-xs text-zinc-500">
                              {new Date(report.generated_at).toLocaleDateString()} • {report.format.toUpperCase()} • {report.record_count} records
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded ${
                          report.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'
                        }`}>
                          {report.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Create Report Dialog - Simplified */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="glass-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Create Report</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Report Name */}
              <div>
                <Label className="text-zinc-400 text-sm">Report Name</Label>
                <Input
                  placeholder="My Report"
                  value={newReport.name}
                  onChange={(e) => setNewReport(prev => ({ ...prev, name: e.target.value }))}
                  className="input-dark mt-1"
                  data-testid="report-name-input"
                />
              </div>

              {/* Datasets */}
              <div>
                <Label className="text-zinc-400 text-sm">Data Sources</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {AVAILABLE_DATASETS.map(dataset => {
                    const isSelected = newReport.datasets.includes(dataset.id);
                    const hasAccess = user?.datasets_access?.includes(dataset.id);
                    return (
                      <button
                        key={dataset.id}
                        onClick={() => hasAccess && handleDatasetToggle(dataset.id)}
                        disabled={!hasAccess}
                        className={`p-2 rounded-lg border text-left text-xs transition-all ${
                          !hasAccess 
                            ? 'border-white/5 text-zinc-600 cursor-not-allowed'
                            : isSelected 
                            ? 'border-indigo-500 bg-indigo-500/10 text-white' 
                            : 'border-white/10 text-zinc-400 hover:bg-white/5'
                        }`}
                      >
                        {dataset.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Format & Schedule Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-sm">Format</Label>
                  <Select value={newReport.format} onValueChange={(v) => setNewReport(prev => ({ ...prev, format: v }))}>
                    <SelectTrigger className="input-dark mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPORT_FORMATS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-sm">Schedule</Label>
                  <Select value={newReport.schedule} onValueChange={(v) => setNewReport(prev => ({ ...prev, schedule: v }))}>
                    <SelectTrigger className="input-dark mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHEDULE_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date Range */}
              <div>
                <Label className="text-zinc-400 text-sm">Date Range</Label>
                <Select value={newReport.dateRange} onValueChange={(v) => setNewReport(prev => ({ ...prev, dateRange: v }))}>
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Email for scheduled */}
              {newReport.schedule !== 'once' && (
                <div>
                  <Label className="text-zinc-400 text-sm">Email Recipients</Label>
                  <Input
                    placeholder="email@example.com"
                    value={newReport.emailRecipients}
                    onChange={(e) => setNewReport(prev => ({ ...prev, emailRecipients: e.target.value }))}
                    className="input-dark mt-1"
                  />
                </div>
              )}

              {/* Include Resolved */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-sm text-zinc-300">Include resolved violations</span>
                <Switch
                  checked={newReport.includeResolved}
                  onCheckedChange={(v) => setNewReport(prev => ({ ...prev, includeResolved: v }))}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <Button variant="ghost" onClick={() => { setCreateDialogOpen(false); resetForm(); }} className="btn-ghost">
                Cancel
              </Button>
              <Button 
                onClick={handleGenerateReport} 
                disabled={generating || !newReport.name || newReport.datasets.length === 0}
                className="btn-primary"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    {newReport.schedule === 'once' ? 'Generate' : 'Schedule'}
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Enhanced Report Schedule Dialog */}
        <Dialog open={enhancedDialogOpen} onOpenChange={setEnhancedDialogOpen}>
          <DialogContent className="bg-zinc-900 border-amber-500/30 max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                Schedule Enhanced Report
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2">
              {/* Name */}
              <div>
                <Label className="text-zinc-400 text-sm">Schedule Name</Label>
                <Input
                  placeholder="e.g., Monthly Portfolio Report"
                  value={newEnhancedSchedule.name}
                  onChange={(e) => setNewEnhancedSchedule(prev => ({ ...prev, name: e.target.value }))}
                  className="input-dark mt-1"
                  data-testid="enhanced-schedule-name"
                />
              </div>
              
              {/* Schedule Type */}
              <div>
                <Label className="text-zinc-400 text-sm">Report For</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setNewEnhancedSchedule(prev => ({ ...prev, scheduleType: 'watchlist', bin_number: '' }))}
                    className={`flex-1 p-2 rounded-lg border text-sm transition-all ${
                      newEnhancedSchedule.scheduleType === 'watchlist'
                        ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                        : 'border-white/10 text-zinc-400 hover:bg-white/5'
                    }`}
                  >
                    Entire Watchlist
                  </button>
                  <button
                    onClick={() => setNewEnhancedSchedule(prev => ({ ...prev, scheduleType: 'property', watchlist_id: '' }))}
                    className={`flex-1 p-2 rounded-lg border text-sm transition-all ${
                      newEnhancedSchedule.scheduleType === 'property'
                        ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                        : 'border-white/10 text-zinc-400 hover:bg-white/5'
                    }`}
                  >
                    Single Property
                  </button>
                </div>
              </div>
              
              {/* Watchlist or BIN */}
              {newEnhancedSchedule.scheduleType === 'watchlist' ? (
                <div>
                  <Label className="text-zinc-400 text-sm">Select Watchlist</Label>
                  <Select 
                    value={newEnhancedSchedule.watchlist_id} 
                    onValueChange={(v) => setNewEnhancedSchedule(prev => ({ ...prev, watchlist_id: v }))}
                  >
                    <SelectTrigger className="input-dark mt-1">
                      <SelectValue placeholder="Choose a watchlist" />
                    </SelectTrigger>
                    <SelectContent>
                      {watchlists.map(wl => (
                        <SelectItem key={wl.id || wl._id} value={wl.id || wl._id}>
                          {wl.name} ({wl.properties?.length || 0} properties)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div>
                  <Label className="text-zinc-400 text-sm">Property BIN</Label>
                  <Input
                    placeholder="e.g., 1084748"
                    value={newEnhancedSchedule.bin_number}
                    onChange={(e) => setNewEnhancedSchedule(prev => ({ ...prev, bin_number: e.target.value }))}
                    className="input-dark mt-1"
                    data-testid="enhanced-bin-input"
                  />
                  <p className="text-xs text-zinc-600 mt-1">Enter the NYC BIN (Building Identification Number)</p>
                </div>
              )}
              
              {/* Frequency */}
              <div>
                <Label className="text-zinc-400 text-sm">Frequency</Label>
                <Select 
                  value={newEnhancedSchedule.frequency} 
                  onValueChange={(v) => setNewEnhancedSchedule(prev => ({ ...prev, frequency: v }))}
                >
                  <SelectTrigger className="input-dark mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENHANCED_FREQUENCY_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Report Type */}
              <div>
                <Label className="text-zinc-400 text-sm">Report Type</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => setNewEnhancedSchedule(prev => ({ ...prev, pdf_report_type: 'executive' }))}
                    className={`flex-1 p-2 rounded-lg border text-sm transition-all ${
                      newEnhancedSchedule.pdf_report_type === 'executive'
                        ? 'border-teal-500 bg-teal-500/10 text-teal-400'
                        : 'border-white/10 text-zinc-400 hover:bg-white/5'
                    }`}
                    data-testid="enhanced-report-type-executive"
                  >
                    Executive
                  </button>
                  <button
                    onClick={() => setNewEnhancedSchedule(prev => ({ ...prev, pdf_report_type: 'extended' }))}
                    className={`flex-1 p-2 rounded-lg border text-sm transition-all ${
                      newEnhancedSchedule.pdf_report_type === 'extended'
                        ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                        : 'border-white/10 text-zinc-400 hover:bg-white/5'
                    }`}
                    data-testid="enhanced-report-type-extended"
                  >
                    Extended
                  </button>
                </div>
              </div>
              
              {/* Email Recipients */}
              <div>
                <Label className="text-zinc-400 text-sm">Email Recipients</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newEnhancedSchedule.emailInput}
                    onChange={(e) => setNewEnhancedSchedule(prev => ({ ...prev, emailInput: e.target.value }))}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddEmailRecipient()}
                    className="input-dark flex-1"
                  />
                  <Button onClick={handleAddEmailRecipient} variant="outline" className="btn-ghost">
                    Add
                  </Button>
                </div>
                {newEnhancedSchedule.email_recipients.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {newEnhancedSchedule.email_recipients.map(email => (
                      <span key={email} className="text-xs bg-white/10 px-2 py-1 rounded flex items-center gap-1">
                        {email}
                        <button 
                          onClick={() => handleRemoveEmailRecipient(email)}
                          className="text-zinc-500 hover:text-rose-400"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Submit */}
              <Button
                onClick={handleCreateEnhancedSchedule}
                disabled={creatingEnhanced}
                className="w-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 hover:from-amber-500/30 hover:to-orange-500/30"
                data-testid="create-enhanced-schedule-submit"
              >
                {creatingEnhanced ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Create Schedule
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
